import {
  base64UrlDecode,
  base64UrlEncode,
  concatBytes,
  hashForDomain,
  randomBytes,
  serializeForDomain,
  sha256,
  toUint8Array,
} from "./encoding.js";
import {
  type RelationshipCardPayload,
  type RelationshipCardVerification,
  type SignedRelationshipCard,
  verifyRelationshipCard,
} from "./card.js";
import {
  type DeviceKeyPairs,
  assertDeviceKeyPairsMatch,
  importP256VerificationKey,
  importRsaWrappingPublicKey,
  signEs256,
  verifyEs256,
} from "./keys.js";
import {
  asRecord,
  assertExactKeys,
  assertHttpsOrigin,
  assertLiteral,
  assertSafeIdentifier,
  fail,
  readArray,
  readInteger,
  readString,
} from "./validation.js";

export const DOCUMENT_ENVELOPE_VERSION = 1 as const;
export const DOCUMENT_CRYPTO_SUITE =
  "BTW1-ES256-RSAOAEP3072-A256GCM" as const;
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const DATA_IV_BYTES = 12;
export const AES_GCM_TAG_BITS = 128;
export const AES_GCM_TAG_BYTES = AES_GCM_TAG_BITS / 8;
export const FILE_KEY_BYTES = 32;
export const RSA_WRAP_BYTES = 384;

export interface DocumentHeader {
  readonly version: typeof DOCUMENT_ENVELOPE_VERSION;
  readonly suite: typeof DOCUMENT_CRYPTO_SUITE;
  readonly issuer: string;
  readonly audience: string;
  readonly relationshipId: string;
  readonly documentId: string;
  readonly revision: number;
  readonly authorDeviceId: string;
  readonly plaintextLength: number;
  readonly dataIv: string;
}

export interface SignedDocumentContent {
  readonly header: DocumentHeader;
  readonly ciphertext: string;
  readonly ciphertextHash: string;
  readonly authorSignature: string;
}

export interface RecipientKeyWrap {
  readonly recipientDeviceId: string;
  readonly wrappedFileKey: string;
}

export interface WrapManifestPayload {
  readonly version: typeof DOCUMENT_ENVELOPE_VERSION;
  readonly suite: typeof DOCUMENT_CRYPTO_SUITE;
  readonly issuer: string;
  readonly audience: string;
  readonly relationshipId: string;
  readonly documentId: string;
  readonly revision: number;
  readonly cardSequence: number;
  readonly contentHash: string;
  readonly signerDeviceId: string;
  readonly wraps: readonly RecipientKeyWrap[];
}

export interface SignedWrapManifest {
  readonly payload: WrapManifestPayload;
  readonly signature: string;
}

export interface SealedDocumentEnvelope {
  readonly version: typeof DOCUMENT_ENVELOPE_VERSION;
  readonly content: SignedDocumentContent;
  readonly manifest: SignedWrapManifest;
}

export interface SealDocumentInput {
  readonly plaintext: BufferSource;
  readonly documentId: string;
  readonly revision: number;
  readonly card: SignedRelationshipCard;
  readonly cardVerification: RelationshipCardVerification;
  readonly authorDeviceKeys: DeviceKeyPairs;
  readonly manifestSignerDeviceKeys?: DeviceKeyPairs;
}

export interface OpenDocumentInput {
  readonly envelope: SealedDocumentEnvelope;
  readonly card: SignedRelationshipCard;
  readonly cardVerification: RelationshipCardVerification;
  readonly recipientDeviceKeys: DeviceKeyPairs;
}

export interface CreateWrapManifestInput {
  readonly content: SignedDocumentContent;
  readonly rawFileKey: Uint8Array<ArrayBuffer>;
  readonly card: SignedRelationshipCard;
  readonly cardVerification: RelationshipCardVerification;
  readonly manifestSignerDeviceKeys: DeviceKeyPairs;
}

export async function sealDocument(
  input: SealDocumentInput,
): Promise<SealedDocumentEnvelope> {
  const plaintext = toUint8Array(input.plaintext);
  if (plaintext.byteLength > MAX_DOCUMENT_BYTES) {
    return fail("DOCUMENT_TOO_LARGE", "Document exceeds the 25 MiB v1 limit");
  }
  assertSafeIdentifier(input.documentId, "document id");
  if (!Number.isSafeInteger(input.revision) || input.revision < 1) {
    return fail("INVALID_DOCUMENT", "Document revision must be a positive integer");
  }

  const card = await verifyRelationshipCard(input.card, input.cardVerification);
  const authorDeviceId = input.authorDeviceKeys.publicKeys.deviceKeyId;
  const author = findCardDevice(card, authorDeviceId, "AUTHOR_NOT_CURRENT");
  await assertDeviceKeyPairsMatch(input.authorDeviceKeys, author.keys.deviceKeyId);
  const manifestSignerKeys = input.manifestSignerDeviceKeys ?? input.authorDeviceKeys;
  const manifestSigner = findCardDevice(
    card,
    manifestSignerKeys.publicKeys.deviceKeyId,
    "MANIFEST_SIGNER_NOT_CURRENT",
  );
  if (manifestSigner.role !== "owner") {
    return fail("MANIFEST_SIGNER_NOT_AUTHORIZED", "Wrap manifest signer must be an owner");
  }
  await assertDeviceKeyPairsMatch(manifestSignerKeys, manifestSigner.keys.deviceKeyId);

  const dataIv = randomBytes(DATA_IV_BYTES);
  const header: DocumentHeader = {
    version: DOCUMENT_ENVELOPE_VERSION,
    suite: DOCUMENT_CRYPTO_SUITE,
    issuer: card.issuer,
    audience: card.audience,
    relationshipId: card.relationshipId,
    documentId: input.documentId,
    revision: input.revision,
    authorDeviceId,
    plaintextLength: plaintext.byteLength,
    dataIv: base64UrlEncode(dataIv),
  };

  const rawFileKey = randomBytes(FILE_KEY_BYTES);
  try {
    const fileKey = await crypto.subtle.importKey(
      "raw",
      rawFileKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
    const ciphertextBytes = new Uint8Array(
      await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: dataIv,
          additionalData: serializeForDomain("document-aad", header),
          tagLength: AES_GCM_TAG_BITS,
        },
        fileKey,
        plaintext,
      ),
    );
    const ciphertextHash = await hashCiphertext(header, ciphertextBytes);
    const authorSignature = await signEs256(
      input.authorDeviceKeys.signingKeyPair.privateKey,
      "document-content-signature",
      { header, ciphertextHash },
    );
    const content: SignedDocumentContent = {
      header,
      ciphertext: base64UrlEncode(ciphertextBytes),
      ciphertextHash,
      authorSignature,
    };
    const manifest = await createManifestForVerifiedCard(
      content,
      rawFileKey,
      card,
      manifestSignerKeys,
    );
    return { version: DOCUMENT_ENVELOPE_VERSION, content, manifest };
  } finally {
    rawFileKey.fill(0);
  }
}

export async function createWrapManifest(
  input: CreateWrapManifestInput,
): Promise<SignedWrapManifest> {
  if (input.rawFileKey.byteLength !== FILE_KEY_BYTES) {
    return fail("INVALID_FILE_KEY", "Raw file key must contain exactly 32 bytes");
  }
  const card = await verifyRelationshipCard(input.card, input.cardVerification);
  const content = normalizeSignedDocumentContent(input.content);
  await verifyContentAgainstCard(content, card);
  return createManifestForVerifiedCard(
    content,
    new Uint8Array(input.rawFileKey),
    card,
    input.manifestSignerDeviceKeys,
  );
}

export async function openDocument(
  input: OpenDocumentInput,
): Promise<Uint8Array<ArrayBuffer>> {
  const card = await verifyRelationshipCard(input.card, input.cardVerification);
  const envelope = normalizeSealedDocumentEnvelope(input.envelope);
  const { content, manifest } = envelope;

  assertHeaderMatchesCard(content.header, card);
  await verifyContentAgainstCard(content, card);
  const expectedContentHash = await signedContentHash(content);
  assertManifestMatchesContentAndCard(manifest.payload, content.header, card, expectedContentHash);

  const manifestSigner = findCardDevice(
    card,
    manifest.payload.signerDeviceId,
    "MANIFEST_SIGNER_NOT_CURRENT",
  );
  if (manifestSigner.role !== "owner") {
    return fail("MANIFEST_SIGNER_NOT_AUTHORIZED", "Wrap manifest signer must be an owner");
  }
  const manifestVerificationKey = await importP256VerificationKey(
    manifestSigner.keys.signingPublicKey,
  );
  if (
    !(await verifyEs256(
      manifestVerificationKey,
      "wrap-manifest-signature",
      manifest.payload,
      manifest.signature,
    ))
  ) {
    return fail("INVALID_MANIFEST_SIGNATURE", "Wrap manifest signature is invalid");
  }

  assertWrapSetMatchesCard(manifest.payload.wraps, card);
  const recipientDeviceId = input.recipientDeviceKeys.publicKeys.deviceKeyId;
  findCardDevice(card, recipientDeviceId, "RECIPIENT_NOT_CURRENT");
  await assertDeviceKeyPairsMatch(input.recipientDeviceKeys, recipientDeviceId);
  const recipientWrap = manifest.payload.wraps.find(
    (wrap) => wrap.recipientDeviceId === recipientDeviceId,
  );
  if (recipientWrap === undefined) {
    return fail("MISSING_RECIPIENT_WRAP", "Current recipient does not have a file-key wrap");
  }

  const wrapContext = makeWrapContext(
    content.header,
    expectedContentHash,
    recipientDeviceId,
  );
  let rawFileKey: Uint8Array<ArrayBuffer>;
  try {
    rawFileKey = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: "RSA-OAEP",
          label: serializeForDomain("file-key-wrap-label", wrapContext),
        },
        input.recipientDeviceKeys.wrappingKeyPair.privateKey,
        base64UrlDecode(recipientWrap.wrappedFileKey, RSA_WRAP_BYTES),
      ),
    );
  } catch {
    return fail("FILE_KEY_UNWRAP_FAILED", "File key could not be unwrapped");
  }
  if (rawFileKey.byteLength !== FILE_KEY_BYTES) {
    rawFileKey.fill(0);
    return fail("FILE_KEY_UNWRAP_FAILED", "Unwrapped file key has an invalid length");
  }

  try {
    const fileKey = await crypto.subtle.importKey(
      "raw",
      rawFileKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"],
    );
    const ciphertext = decodeCiphertext(content);
    let plaintext: Uint8Array<ArrayBuffer>;
    try {
      plaintext = new Uint8Array(
        await crypto.subtle.decrypt(
          {
            name: "AES-GCM",
            iv: base64UrlDecode(content.header.dataIv, DATA_IV_BYTES),
            additionalData: serializeForDomain("document-aad", content.header),
            tagLength: AES_GCM_TAG_BITS,
          },
          fileKey,
          ciphertext,
        ),
      );
    } catch {
      return fail("DOCUMENT_DECRYPT_FAILED", "Document authentication failed");
    }
    if (plaintext.byteLength !== content.header.plaintextLength) {
      plaintext.fill(0);
      return fail("DOCUMENT_DECRYPT_FAILED", "Document length does not match its header");
    }
    return plaintext;
  } finally {
    rawFileKey.fill(0);
  }
}

export function normalizeSealedDocumentEnvelope(value: unknown): SealedDocumentEnvelope {
  const record = asRecord(value, "sealed document envelope");
  assertExactKeys(record, ["version", "content", "manifest"], "sealed document envelope");
  assertLiteral(record.version, DOCUMENT_ENVELOPE_VERSION, "sealed document envelope.version");
  return {
    version: DOCUMENT_ENVELOPE_VERSION,
    content: normalizeSignedDocumentContent(record.content),
    manifest: normalizeSignedWrapManifest(record.manifest),
  };
}

async function createManifestForVerifiedCard(
  content: SignedDocumentContent,
  rawFileKey: Uint8Array<ArrayBuffer>,
  card: RelationshipCardPayload,
  signerKeys: DeviceKeyPairs,
): Promise<SignedWrapManifest> {
  const signer = findCardDevice(
    card,
    signerKeys.publicKeys.deviceKeyId,
    "MANIFEST_SIGNER_NOT_CURRENT",
  );
  if (signer.role !== "owner") {
    return fail("MANIFEST_SIGNER_NOT_AUTHORIZED", "Wrap manifest signer must be an owner");
  }
  await assertDeviceKeyPairsMatch(signerKeys, signer.keys.deviceKeyId);
  const contentHash = await signedContentHash(content);
  const wraps: RecipientKeyWrap[] = [];
  for (const device of card.devices) {
    const publicKey = await importRsaWrappingPublicKey(device.keys.wrappingPublicKey);
    const wrappedFileKey = await crypto.subtle.encrypt(
      {
        name: "RSA-OAEP",
        label: serializeForDomain(
          "file-key-wrap-label",
          makeWrapContext(content.header, contentHash, device.keys.deviceKeyId),
        ),
      },
      publicKey,
      rawFileKey,
    );
    wraps.push({
      recipientDeviceId: device.keys.deviceKeyId,
      wrappedFileKey: base64UrlEncode(wrappedFileKey),
    });
  }
  wraps.sort((left, right) => compareCodeUnits(left.recipientDeviceId, right.recipientDeviceId));
  const payload: WrapManifestPayload = {
    version: DOCUMENT_ENVELOPE_VERSION,
    suite: DOCUMENT_CRYPTO_SUITE,
    issuer: card.issuer,
    audience: card.audience,
    relationshipId: card.relationshipId,
    documentId: content.header.documentId,
    revision: content.header.revision,
    cardSequence: card.sequence,
    contentHash,
    signerDeviceId: signer.keys.deviceKeyId,
    wraps,
  };
  return {
    payload,
    signature: await signEs256(
      signerKeys.signingKeyPair.privateKey,
      "wrap-manifest-signature",
      payload,
    ),
  };
}

async function verifyContentAgainstCard(
  content: SignedDocumentContent,
  card: RelationshipCardPayload,
): Promise<void> {
  assertHeaderMatchesCard(content.header, card);
  const ciphertext = decodeCiphertext(content);
  const expectedHash = await hashCiphertext(content.header, ciphertext);
  if (content.ciphertextHash !== expectedHash) {
    return fail("CIPHERTEXT_HASH_MISMATCH", "Ciphertext hash does not match");
  }
  const author = findCardDevice(card, content.header.authorDeviceId, "AUTHOR_NOT_CURRENT");
  const authorVerificationKey = await importP256VerificationKey(author.keys.signingPublicKey);
  if (
    !(await verifyEs256(
      authorVerificationKey,
      "document-content-signature",
      { header: content.header, ciphertextHash: content.ciphertextHash },
      content.authorSignature,
    ))
  ) {
    return fail("INVALID_CONTENT_SIGNATURE", "Document content signature is invalid");
  }
}

function normalizeSignedDocumentContent(value: unknown): SignedDocumentContent {
  const record = asRecord(value, "signed document content");
  assertExactKeys(
    record,
    ["header", "ciphertext", "ciphertextHash", "authorSignature"],
    "signed document content",
  );
  const header = normalizeDocumentHeader(record.header);
  const ciphertext = readString(
    record,
    "ciphertext",
    "signed document content",
    maximumBase64UrlLength(MAX_DOCUMENT_BYTES + AES_GCM_TAG_BYTES),
  );
  const ciphertextHash = readString(record, "ciphertextHash", "signed document content", 64);
  const authorSignature = readString(record, "authorSignature", "signed document content", 128);
  if (base64UrlDecode(ciphertextHash, 32).byteLength !== 32) {
    return fail("INVALID_DOCUMENT", "Ciphertext hash has an invalid length");
  }
  if (base64UrlDecode(authorSignature, 64).byteLength !== 64) {
    return fail("INVALID_DOCUMENT", "Content signature has an invalid length");
  }
  const ciphertextBytes = base64UrlDecode(ciphertext, MAX_DOCUMENT_BYTES + AES_GCM_TAG_BYTES);
  if (ciphertextBytes.byteLength !== header.plaintextLength + AES_GCM_TAG_BYTES) {
    return fail("INVALID_DOCUMENT", "Ciphertext length does not match its header");
  }
  return { header, ciphertext, ciphertextHash, authorSignature };
}

function normalizeDocumentHeader(value: unknown): DocumentHeader {
  const record = asRecord(value, "document header");
  assertExactKeys(
    record,
    [
      "version",
      "suite",
      "issuer",
      "audience",
      "relationshipId",
      "documentId",
      "revision",
      "authorDeviceId",
      "plaintextLength",
      "dataIv",
    ],
    "document header",
  );
  assertLiteral(record.version, DOCUMENT_ENVELOPE_VERSION, "document header.version");
  assertLiteral(record.suite, DOCUMENT_CRYPTO_SUITE, "document header.suite");
  const issuer = readString(record, "issuer", "document header", 512);
  const audience = readString(record, "audience", "document header", 512);
  const relationshipId = readString(record, "relationshipId", "document header", 256);
  const documentId = readString(record, "documentId", "document header", 256);
  const revision = readInteger(record, "revision", "document header", 1);
  const authorDeviceId = readString(record, "authorDeviceId", "document header", 64);
  const plaintextLength = readInteger(
    record,
    "plaintextLength",
    "document header",
    0,
    MAX_DOCUMENT_BYTES,
  );
  const dataIv = readString(record, "dataIv", "document header", 32);
  assertHttpsOrigin(issuer, "document header.issuer");
  assertHttpsOrigin(audience, "document header.audience");
  assertSafeIdentifier(relationshipId, "document header.relationshipId");
  assertSafeIdentifier(documentId, "document header.documentId");
  if (
    base64UrlDecode(authorDeviceId, 32).byteLength !== 32 ||
    base64UrlDecode(dataIv, DATA_IV_BYTES).byteLength !== DATA_IV_BYTES
  ) {
    return fail("INVALID_DOCUMENT", "Document header key id or IV is invalid");
  }
  return {
    version: DOCUMENT_ENVELOPE_VERSION,
    suite: DOCUMENT_CRYPTO_SUITE,
    issuer,
    audience,
    relationshipId,
    documentId,
    revision,
    authorDeviceId,
    plaintextLength,
    dataIv,
  };
}

function normalizeSignedWrapManifest(value: unknown): SignedWrapManifest {
  const record = asRecord(value, "signed wrap manifest");
  assertExactKeys(record, ["payload", "signature"], "signed wrap manifest");
  const signature = readString(record, "signature", "signed wrap manifest", 128);
  if (base64UrlDecode(signature, 64).byteLength !== 64) {
    return fail("INVALID_MANIFEST", "Wrap manifest signature has an invalid length");
  }
  return { payload: normalizeWrapManifestPayload(record.payload), signature };
}

function normalizeWrapManifestPayload(value: unknown): WrapManifestPayload {
  const record = asRecord(value, "wrap manifest payload");
  assertExactKeys(
    record,
    [
      "version",
      "suite",
      "issuer",
      "audience",
      "relationshipId",
      "documentId",
      "revision",
      "cardSequence",
      "contentHash",
      "signerDeviceId",
      "wraps",
    ],
    "wrap manifest payload",
  );
  assertLiteral(record.version, DOCUMENT_ENVELOPE_VERSION, "wrap manifest payload.version");
  assertLiteral(record.suite, DOCUMENT_CRYPTO_SUITE, "wrap manifest payload.suite");
  const issuer = readString(record, "issuer", "wrap manifest payload", 512);
  const audience = readString(record, "audience", "wrap manifest payload", 512);
  const relationshipId = readString(record, "relationshipId", "wrap manifest payload", 256);
  const documentId = readString(record, "documentId", "wrap manifest payload", 256);
  const revision = readInteger(record, "revision", "wrap manifest payload", 1);
  const cardSequence = readInteger(record, "cardSequence", "wrap manifest payload", 1);
  const contentHash = readString(record, "contentHash", "wrap manifest payload", 64);
  const signerDeviceId = readString(record, "signerDeviceId", "wrap manifest payload", 64);
  assertHttpsOrigin(issuer, "wrap manifest payload.issuer");
  assertHttpsOrigin(audience, "wrap manifest payload.audience");
  assertSafeIdentifier(relationshipId, "wrap manifest payload.relationshipId");
  assertSafeIdentifier(documentId, "wrap manifest payload.documentId");
  if (
    base64UrlDecode(contentHash, 32).byteLength !== 32 ||
    base64UrlDecode(signerDeviceId, 32).byteLength !== 32
  ) {
    return fail("INVALID_MANIFEST", "Wrap manifest hash or signer id is invalid");
  }
  const wrapInputs = readArray(record, "wraps", "wrap manifest payload", 100);
  if (wrapInputs.length === 0) {
    return fail("INVALID_MANIFEST", "Wrap manifest must contain a recipient wrap");
  }
  const wraps: RecipientKeyWrap[] = [];
  const recipients = new Set<string>();
  let previousRecipient = "";
  for (const [index, wrapInput] of wrapInputs.entries()) {
    const wrapRecord = asRecord(wrapInput, `recipient wrap ${index}`);
    assertExactKeys(wrapRecord, ["recipientDeviceId", "wrappedFileKey"], `recipient wrap ${index}`);
    const recipientDeviceId = readString(wrapRecord, "recipientDeviceId", `recipient wrap ${index}`, 64);
    const wrappedFileKey = readString(wrapRecord, "wrappedFileKey", `recipient wrap ${index}`, 1024);
    if (
      base64UrlDecode(recipientDeviceId, 32).byteLength !== 32 ||
      base64UrlDecode(wrappedFileKey, RSA_WRAP_BYTES).byteLength !== RSA_WRAP_BYTES
    ) {
      return fail("INVALID_MANIFEST", "Recipient wrap has an invalid key id or ciphertext length");
    }
    if (recipients.has(recipientDeviceId) || (previousRecipient !== "" && previousRecipient >= recipientDeviceId)) {
      return fail("INVALID_MANIFEST", "Recipient wraps must be unique and sorted");
    }
    recipients.add(recipientDeviceId);
    previousRecipient = recipientDeviceId;
    wraps.push({ recipientDeviceId, wrappedFileKey });
  }
  return {
    version: DOCUMENT_ENVELOPE_VERSION,
    suite: DOCUMENT_CRYPTO_SUITE,
    issuer,
    audience,
    relationshipId,
    documentId,
    revision,
    cardSequence,
    contentHash,
    signerDeviceId,
    wraps,
  };
}

function assertHeaderMatchesCard(
  header: DocumentHeader,
  card: RelationshipCardPayload,
): void {
  if (header.issuer !== card.issuer) {
    fail("WRONG_ISSUER", "Document issuer does not match the current card");
  }
  if (header.audience !== card.audience) {
    fail("WRONG_AUDIENCE", "Document audience does not match the current card");
  }
  if (header.relationshipId !== card.relationshipId) {
    fail("WRONG_RELATIONSHIP", "Document relationship does not match the current card");
  }
}

function assertManifestMatchesContentAndCard(
  manifest: WrapManifestPayload,
  header: DocumentHeader,
  card: RelationshipCardPayload,
  contentHash: string,
): void {
  if (
    manifest.issuer !== header.issuer ||
    manifest.audience !== header.audience ||
    manifest.relationshipId !== header.relationshipId ||
    manifest.documentId !== header.documentId ||
    manifest.revision !== header.revision ||
    manifest.contentHash !== contentHash
  ) {
    fail("MANIFEST_CONTENT_MISMATCH", "Wrap manifest does not bind this document content");
  }
  if (manifest.cardSequence !== card.sequence) {
    fail("WRONG_CARD_SEQUENCE", "Wrap manifest is not bound to the current card sequence");
  }
}

function assertWrapSetMatchesCard(
  wraps: readonly RecipientKeyWrap[],
  card: RelationshipCardPayload,
): void {
  const expected = card.devices.map((device) => device.keys.deviceKeyId).sort();
  const actual = wraps.map((wrap) => wrap.recipientDeviceId);
  if (
    actual.length !== expected.length ||
    actual.some((deviceId, index) => deviceId !== expected[index])
  ) {
    fail("WRAP_SET_MISMATCH", "Wrap manifest does not exactly match the current card devices");
  }
}

function findCardDevice(
  card: RelationshipCardPayload,
  deviceKeyId: string,
  errorCode: string,
): RelationshipCardPayload["devices"][number] {
  const device = card.devices.find((candidate) => candidate.keys.deviceKeyId === deviceKeyId);
  if (device === undefined) {
    return fail(errorCode, "Required device key is not on the current card");
  }
  return device;
}

function decodeCiphertext(content: SignedDocumentContent): Uint8Array<ArrayBuffer> {
  const ciphertext = base64UrlDecode(
    content.ciphertext,
    MAX_DOCUMENT_BYTES + AES_GCM_TAG_BYTES,
  );
  if (ciphertext.byteLength !== content.header.plaintextLength + AES_GCM_TAG_BYTES) {
    return fail("INVALID_DOCUMENT", "Ciphertext length does not match the document header");
  }
  return ciphertext;
}

async function hashCiphertext(
  header: DocumentHeader,
  ciphertext: Uint8Array<ArrayBuffer>,
): Promise<string> {
  return base64UrlEncode(
    await sha256(
      concatBytes(
        serializeForDomain("document-ciphertext-hash-context", header),
        ciphertext,
      ),
    ),
  );
}

async function signedContentHash(content: SignedDocumentContent): Promise<string> {
  return hashForDomain("signed-document-content-binding", {
    header: content.header,
    ciphertextHash: content.ciphertextHash,
    authorSignature: content.authorSignature,
  });
}

function makeWrapContext(
  header: DocumentHeader,
  contentHash: string,
  recipientDeviceId: string,
): object {
  return {
    version: DOCUMENT_ENVELOPE_VERSION,
    suite: DOCUMENT_CRYPTO_SUITE,
    issuer: header.issuer,
    audience: header.audience,
    relationshipId: header.relationshipId,
    documentId: header.documentId,
    revision: header.revision,
    contentHash,
    recipientDeviceId,
  };
}

function maximumBase64UrlLength(maximumBytes: number): number {
  return Math.ceil((maximumBytes * 4) / 3);
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
